import { FunctionComponent } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title, Button, UtilButton, DropdownMenu } from 'UI';
import useFileDownload from '@/hooks/useFiledDownload';
import EftFundingTransactionContainer from './EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './EftWithdrawTransactionContainer';
import InteracMoneyRequestTransactionContainer from './InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './InteracBulkPayoutTransactionContainer';
import CreditCardTransactionContainer from './CreditCardTransactionContainer';
import styles from './Transaction.module.scss';
import { Box, TransactionType } from 'vopay';

type FC = FunctionComponent;

const Transaction: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { printFile, downloadPdfPage } = useFileDownload();
  const history = useHistory();
  const { TransactionID } = useParams<{ TransactionID: string }>();
  const query = new URLSearchParams(useLocation().search);
  const type = decodeURIComponent(query.get('type') || '');

  const title = t('transactionOverview', { defaultValue: 'Transaction Overview' });

  return (
    <div className={styles.wrapper}>
      <Box flx="col" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            history.goBack();
          }}
        >
          {tGlobal('Buttons.back', { defaultValue: 'Back' })}
        </UtilButton>

        <Box width="100%" flx="row" jc="sb">
          <Title>{t('transactionOverview', { defaultValue: 'Transaction Overview' })}</Title>

          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button iconRight="keyboard_arrow_down">
                {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Content align="end">
              <DropdownMenu.Item
                onSelect={() => {
                  downloadPdfPage('transaction-overview', title);
                }}
              >
                {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={() => {
                  printFile('transaction-overview');
                }}
              >
                {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </Box>
      </Box>

      <div id="transaction-overview" className={styles.overviewWrapper}>
        {type === TransactionType.EFT_FUNDING && (
          <EftFundingTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.EFT_WITHDRAWAL && (
          <EftWithdrawTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.INTERACT_MONEY_REQUEST && (
          <InteracMoneyRequestTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.INTERACT_BULK_PAYOUT && (
          <InteracBulkPayoutTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.CREDIT_CARD && (
          <CreditCardTransactionContainer TransactionID={TransactionID} Type={type} />
        )}
      </div>
    </div>
  );
};

export default Transaction;
